/**
 * Merkezi conversion tracking: GA4 + Meta Pixel
 *
 * ID yoksa hiçbir şey crash olmaz.
 * Development'ta console.debug ile event loglansın.
 * Production'da sessiz.
 */

const IS_DEV = process.env.NODE_ENV !== "production";
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// ── Funnel backend recording ─────────────────────────────────
const SESSION_FUNNEL_KEY = "dk_funnel_pv_sent";

function recordFunnel(event) {
  const url = `${BACKEND_URL}/api/funnel/event`;
  const body = JSON.stringify({ event });
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch (_) { /* fire-and-forget */ }
  dbg("funnel:" + event, {});
}

// ── Debug ────────────────────────────────────────────────────
function dbg(name, payload) {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.debug("[tracking]", name, payload ?? "");
  }
}

/** @param {{ language?: 'tr'|'en' }} [extra] */
function withLanguage(extra) {
  const l = extra?.language;
  if (l === "tr" || l === "en") return { language: l };
  return {};
}

// ── GA4 ──────────────────────────────────────────────────────
function loadGA(id) {
  if (!id || document.getElementById("ga4-gtag")) return;
  window.dataLayer = window.dataLayer || [];
  // gtag zorunlu olarak native `arguments` nesnesini kullanır — rest param çalışmaz
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // SPA: page_view manuel yönetilir, otomatik gönderimi kapat
  window.gtag("config", id, { send_page_view: false });
  const s = document.createElement("script");
  s.id = "ga4-gtag";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
}

function ga(event, params) {
  if (!GA_ID || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", event, params || {});
  } catch (_) { /* crash olmasın */ }
}

// ── Meta Pixel ───────────────────────────────────────────────
function loadPixel(id) {
  if (!id || typeof window.fbq === "function") return;
  const n = function() {
    // eslint-disable-next-line prefer-rest-params
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  if (first && first.parentNode) first.parentNode.insertBefore(t, first);
  window.fbq("init", id);
}

function pixel(event, params) {
  if (!PIXEL_ID || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", event, params || {});
  } catch (_) { /* crash olmasın */ }
}

// ── Public API ───────────────────────────────────────────────

/** Layout mount'unda bir kez çağır. */
export function initTracking() {
  if (GA_ID) loadGA(GA_ID);
  if (PIXEL_ID) loadPixel(PIXEL_ID);
}

/** Route değiştiğinde çağır. */
export function trackPageView(path) {
  dbg("page_view", { path });
  if (GA_ID && typeof window.gtag === "function") {
    window.gtag("config", GA_ID, { page_path: path });
  }
  pixel("PageView");
  // Funnel: sessionStorage dedup — aynı sekme içinde bir kez sayılsın
  try {
    if (!sessionStorage.getItem(SESSION_FUNNEL_KEY)) {
      sessionStorage.setItem(SESSION_FUNNEL_KEY, "1");
      recordFunnel("page_view");
    }
  } catch (_) { recordFunnel("page_view"); }
}

/** Dosya seçildi ama henüz analiz başlamadı. */
export function trackQuoteUpload(file, extra = {}) {
  const ext = file?.name ? "." + file.name.split(".").pop().toLowerCase() : "unknown";
  const payload = { file_ext: ext, ...withLanguage(extra) };
  dbg("quote_upload_started", payload);
  ga("quote_upload_started", payload);
  recordFunnel("quote_upload_started");
}

/** /quote/analyze başarılı döndü. */
export function trackQuoteAnalysis(data, extra = {}) {
  const ps = data?.customer_print_settings;
  const pricing = data?.pricing;
  const payload = {
    pricing_status: data?.pricing_status,
    material: ps?.material,
    confidence: pricing?.confidence,
    price_low: pricing?.price_low_tl,
    price_high: pricing?.price_high_tl,
    ...withLanguage(extra),
  };
  dbg("quote_analysis_success", payload);
  ga("quote_analysis_success", payload);
  if (data?.pricing_status !== "pending_review") {
    pixel("CustomizeProduct", { content_category: ps?.material });
  }
  recordFunnel("quote_analysis_success");
}

/** /quote/reprice başarılı döndü. */
export function trackQuoteReprice(data, printCfg, extra = {}) {
  const pricing = data?.pricing;
  const payload = {
    material: data?.customer_print_settings?.material,
    infill_percent: printCfg?.infillPercent,
    wall_count: printCfg?.wallCount,
    layer_height_mm: printCfg?.layerHeightMm,
    quality_profile: printCfg?.qualityProfile,
    price_low: pricing?.price_low_tl,
    price_high: pricing?.price_high_tl,
    ...withLanguage(extra),
  };
  dbg("quote_reprice", payload);
  ga("quote_reprice", payload);
}

/** Teklif formu başarıyla gönderildi. */
export function trackQuoteSubmit(form, backendEstimate, printCfg, extra = {}) {
  const pricing = backendEstimate?.pricing;
  const payload = {
    material: form?.material,
    quantity: form?.quantity,
    pricing_status: backendEstimate?.pricing_status,
    infill_percent: printCfg?.infillPercent,
    quality_profile: printCfg?.qualityProfile,
    ...withLanguage(extra),
  };
  dbg("quote_submit_success", payload);
  ga("quote_submit_success", payload);
  pixel("Lead", {
    content_category: form?.material,
    value: pricing?.price_low_tl,
    currency: "TRY",
  });
  recordFunnel("quote_submit_success");
}

/** Herhangi bir WhatsApp linkine tıklandı. */
export function trackWhatsappClick(source, extra = {}) {
  const payload = { source: source || "unknown", ...withLanguage(extra) };
  dbg("whatsapp_click", payload);
  ga("whatsapp_click", payload);
  pixel("Contact");
}

/** Tema toggle. */
export function trackThemeToggle(newTheme, extra = {}) {
  const payload = { theme: newTheme, ...withLanguage(extra) };
  dbg("theme_toggle", payload);
  ga("theme_toggle", payload);
}

/** 3MF içinden Bambu baskı ayarları okundu (dosya başına bir kez). */
export function track3mfSettingsDetected(extra = {}) {
  const payload = {
    infill_percent: extra.infill_percent,
    wall_count: extra.wall_count,
    layer_height_mm: extra.layer_height_mm,
    quality_profile: extra.quality_profile != null ? String(extra.quality_profile) : "",
    ...withLanguage(extra),
  };
  dbg("3mf_settings_detected", payload);
  ga("3mf_settings_detected", payload);
}

/** PriceEstimateCard / funnel banner → teklif CTA butonuna tıklandı. */
export function trackQuoteCTAClick(extra = {}) {
  const payload = { ...withLanguage(extra) };
  dbg("quote_cta_click", payload);
  ga("quote_cta_click", payload);
}

/** Home hero CTA butonlarından birine tıklandı. */
export function trackHeroCTAClick(variant, extra = {}) {
  const payload = { variant: variant || "primary", ...withLanguage(extra) };
  dbg("hero_cta_click", payload);
  ga("hero_cta_click", payload);
}
